import React from 'react';
import Navbar from '../Homepage/Navbar';
import Footer from '../Homepage/Footer';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserGraduate,
    faChalkboardTeacher,
    faHandshake,
    faHandsHelping,
    faBullhorn,
    faEnvelope,
    faMapMarkerAlt,
    faPaperPlane,
    faPhone
} from '@fortawesome/free-solid-svg-icons';

const SectionCard = ({ title, icon, description, listItems, delay, color }) => {
    const colorClasses = {
        blue: {
            border: "hover:border-blue-500/50",
            shadow: "hover:shadow-blue-500/20",
            bg: "bg-blue-500/10",
            bgHover: "group-hover:bg-blue-500/20",
            text: "text-blue-400",
            dot: "bg-blue-500",
            blob: "bg-blue-500/10"
        },
        green: {
            border: "hover:border-green-500/50",
            shadow: "hover:shadow-green-500/20",
            bg: "bg-green-500/10",
            bgHover: "group-hover:bg-green-500/20",
            text: "text-green-400",
            dot: "bg-green-500",
            blob: "bg-green-500/10"
        },
        purple: {
            border: "hover:border-purple-500/50",
            shadow: "hover:shadow-purple-500/20",
            bg: "bg-purple-500/10",
            bgHover: "group-hover:bg-purple-500/20",
            text: "text-purple-400",
            dot: "bg-purple-500",
            blob: "bg-purple-500/10"
        },
        yellow: {
            border: "hover:border-yellow-500/50",
            shadow: "hover:shadow-yellow-500/20",
            bg: "bg-yellow-500/10",
            bgHover: "group-hover:bg-yellow-500/20",
            text: "text-yellow-400",
            dot: "bg-yellow-500",
            blob: "bg-yellow-500/10"
        },
        pink: {
            border: "hover:border-pink-500/50",
            shadow: "hover:shadow-pink-500/20",
            bg: "bg-pink-500/10",
            bgHover: "group-hover:bg-pink-500/20",
            text: "text-pink-400",
            dot: "bg-pink-500",
            blob: "bg-pink-500/10"
        }
    };

    const theme = colorClasses[color] || colorClasses.blue;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: delay }}
            viewport={{ once: true }}
            className={`bg-dark-tertiary p-8 rounded-2xl shadow-lg border border-white/5 ${theme.border} transition-all duration-300 ${theme.shadow} group relative overflow-hidden`}
        >
            <div className={`absolute top-0 right-0 w-32 h-32 ${theme.blob} rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none ${theme.bgHover} transition-all duration-500`}></div>

            <div className={`w-16 h-16 ${theme.bg} rounded-xl flex items-center justify-center mb-6 ${theme.text} group-hover:scale-110 transition-transform duration-300`}>
                <FontAwesomeIcon icon={icon} className="text-3xl" />
            </div>

            <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-nvcti-orange transition-colors">{title}</h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
                {description}
            </p>

            <ul className="space-y-3">
                {listItems.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-300">
                        <span className={`mr-2 mt-1.5 w-1.5 h-1.5 ${theme.dot} rounded-full flex-shrink-0`}></span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </motion.div>
    );
};

const GetInvolved = () => {
    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    return (
        <div className="min-h-screen flex flex-col bg-dark-secondary">
            <Navbar />

            {/* Hero Section */}
            <main className="flex-grow pt-24 pb-20">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeIn}
                        className="text-center max-w-4xl mx-auto mb-20"
                    >
                        <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
                            Get Involved with <span className="text-transparent bg-clip-text bg-gradient-to-r from-nvcti-orange to-red-500">NVCTI</span>
                        </h1>
                        <p className="text-xl text-gray-300 leading-relaxed">
                            Nehru Virtual Centre for Training and Innovation (NVCTI) at IIT (ISM) Dhanbad is committed to fostering innovation, skill development, entrepreneurship, and industry–academia collaboration. We invite students, professionals, institutions, and organizations to actively engage with us and contribute to building a skilled and future-ready ecosystem.
                        </p>
                    </motion.div>

                    {/* Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">

                        <SectionCard
                            title="For Students"
                            icon={faUserGraduate}
                            description="NVCTI offers opportunities for students to enhance their technical, professional, and entrepreneurial skills through structured programs and hands-on learning."
                            listItems={[
                                "Enrolling in skill development and certification programs",
                                "Participating in innovation and entrepreneurship initiatives",
                                "Attending workshops, seminars, and expert talks",
                                "Gaining practical exposure through projects and internships"
                            ]}
                            delay={0.1}
                            color="blue"
                        />

                        <SectionCard
                            title="For Trainers & Mentors"
                            icon={faChalkboardTeacher}
                            description="We welcome experienced professionals, academicians, and industry experts to collaborate with NVCTI in delivering high-quality training and mentorship."
                            listItems={[
                                "Conducting training programs and workshops",
                                "Mentoring students and startups",
                                "Delivering guest lectures and expert sessions",
                                "Supporting research, innovation, and skill initiatives"
                            ]}
                            delay={0.2}
                            color="green"
                        />

                        <SectionCard
                            title="Industry Partners"
                            icon={faHandshake}
                            description="NVCTI actively collaborates with industries, startups, government bodies, and academic institutions to strengthen industry–academia linkages."
                            listItems={[
                                "Joint training and certification programs",
                                "Industry-aligned curriculum development",
                                "Collaborative research and innovation projects",
                                "Internship, placement, and skill enhancement initiatives"
                            ]}
                            delay={0.3}
                            color="purple"
                        />



                        <SectionCard
                            title="Volunteering"
                            icon={faHandsHelping}
                            description="Individuals and organizations interested in supporting education, innovation, and skill development can contribute through volunteering and outreach activities."
                            listItems={[
                                "Academic and program support",
                                "Event coordination and outreach",
                                "Student mentoring and guidance",
                                "Innovation and entrepreneurship promotion"
                            ]}
                            delay={0.4}
                            color="yellow"
                        />

                        <SectionCard
                            title="Events & Initiatives"
                            icon={faBullhorn}
                            description="Stay connected with NVCTI through our various programs designed to help you engage, network, and collaborate with leading minds."
                            listItems={[
                                "Skill development workshops",
                                "Entrepreneurship and innovation programs",
                                "Faculty development programs",
                                "Seminars, conferences, and industry interactions"
                            ]}
                            delay={0.5}
                            color="pink"
                        />
                    </div>

                    {/* Contact Section */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="bg-dark-tertiary rounded-3xl shadow-2xl border border-white/5 overflow-hidden max-w-4xl mx-auto"
                    >
                        <div className="p-10 lg:p-14 flex flex-col justify-center items-center text-center relative bg-gradient-to-br from-gray-900 to-gray-800">
                            <div className="absolute top-0 left-0 w-64 h-64 bg-nvcti-orange/10 rounded-full blur-3xl -ml-20 -mt-20"></div>

                            <h2 className="text-3xl font-bold text-white mb-6 relative z-10">Reach Us</h2>
                            <div className="w-24 h-1 bg-nvcti-orange mb-10 mx-auto relative z-10 rounded-full"></div>
                            <p className="text-gray-400 mb-10 leading-relaxed relative z-10 max-w-2xl">
                                If you are interested in getting involved with NVCTI, IIT (ISM) Dhanbad, please reach out to us. We would love to hear from you.
                            </p>

                            <div className="w-full max-w-2xl text-left bg-dark-secondary/50 rounded-2xl border border-white/10 p-8 backdrop-blur-sm relative z-10 mx-auto">
                                {/* Address Section */}
                                <div className="flex gap-6 items-start">
                                    <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center flex-shrink-0 text-pink-500 mt-1">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xl" />
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className="text-white font-bold text-lg mb-2">Address</h4>
                                        <p className="text-gray-400 leading-relaxed text-sm">
                                            NVCTI, 2nd Floor, i2h Building, IIT (ISM) Dhanbad, Jharkhand, India 826004
                                        </p>
                                    </div>
                                </div>

                                <div className="h-px bg-gray-600 w-full my-8"></div>

                                {/* Email Section */}
                                <div className="flex gap-6 items-start">
                                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 text-blue-500 mt-1">
                                        <FontAwesomeIcon icon={faEnvelope} className="text-xl" />
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className="text-white font-bold text-lg mb-2">Email</h4>
                                        <a href="mailto:nvcti@iitism.ac.in" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">
                                            nvcti@iitism.ac.in
                                        </a>
                                    </div>
                                </div>

                                <div className="h-px bg-gray-600 w-full my-8"></div>

                                {/* Phone Section */}
                                <div className="flex gap-6 items-start">
                                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0 text-green-500 mt-1">
                                        <FontAwesomeIcon icon={faPhone} className="text-xl" />
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className="text-white font-bold text-lg mb-2">Phone</h4>
                                        <a href="tel:7707018426" className="text-green-400 hover:text-green-300 transition-colors font-medium">
                                            7707018426
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </main >

            <Footer />
        </div >
    );
};

export default GetInvolved;
